// src/shared/components/layout/Sidebar.jsx
// ✅ تصميم جديد — بدون رموز، مع بحث سريع وتقسيم المجموعات

import React, { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../features/auth/context/AuthContext';
import styles from './Sidebar.module.css';

/* ── عناصر التنقل مقسمة إلى مجموعات ── */
const NAV_GROUPS = [
  {
    label: 'الرئيسية',
    items: [
      { section: 'bookings', path: '/',        label: 'سجل الحجوزات' },
      { section: 'tasks',    path: '/tasks',   label: 'لوحة المهام'  },
      { section: 'treasury', path: '/treasury',label: 'نظام الخزينة' },
    ],
  },
  {
    label: 'التقارير',
    items: [
      { section: 'reports',            path: '/reports',            label: 'التقارير المالية'  },
      { section: 'expenses',           path: '/expenses',           label: 'إدارة المصاريف'   },
      { section: 'expense_categories', path: '/expense-categories', label: 'حسابات المصاريف'  },
      { section: 'client_report',      path: '/client-report',      label: 'كشف حساب عميل'    },
      { section: 'bookings', path: '/documents', label: 'منشئ المستندات' }

    ],
  },
  {
    label: 'الإدارة',
    items: [
      { section: 'clients',      path: '/clients',       label: 'إدارة العملاء'       },
      { section: 'users',        path: '/users',         label: 'المستخدمون'          },
      { section: 'activity_log', path: '/activity-log',  label: 'سجل النشاط'          },
      { section: 'settings',     path: '/settings',      label: 'الإعدادات'           },
    ],
  },
];

const Sidebar = ({ isOpen, onClose, user, onLogout }) => {
  const { canAccess } = useAuth();
  const [search, setSearch] = useState('');

  const initials = user?.first_name?.[0]
    || user?.username?.[0]?.toUpperCase()
    || '؟';

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.username;

  /* فلترة الروابط حسب البحث */
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return NAV_GROUPS;
    const q = search.trim().toLowerCase();
    return NAV_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => item.label.includes(q)),
    })).filter(group => group.items.length > 0);
  }, [search]);

  return (
    <nav
      className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}
      aria-label="القائمة الرئيسية"
    >
      {/* ── هيدر السايدبار ── */}
      <div className={styles.sidebarHeader} onClick={onClose}>
        <span className={styles.brandName}>الاستوديو</span>
        <button
          className={styles.closeBtn}
          aria-label="إغلاق القائمة"
          type="button"
        >
          ✕
        </button>
      </div>

      {/* ── بحث سريع ── */}
      <div className={styles.searchBox}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="بحث في القائمة..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onClick={e => e.stopPropagation()}
        />
      </div>

      {/* ── روابط التنقل ── */}
      <div className={styles.nav}>
        {filteredGroups.map(group => (
          <div key={group.label}>
            {/* عنوان المجموعة — يُخفى أثناء البحث إذا كانت المجموعات مدمجة */}
            {!search.trim() && (
              <div className={styles.navGroup}>{group.label}</div>
            )}

            {group.items.map(({ section, path, label, badge }) =>
              canAccess(section) ? (
                <NavLink
                  key={section}
                  to={path}
                  end={path === '/'}
                  onClick={() => { onClose(); setSearch(''); }}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.active : ''}`
                  }
                >
                  <span>{label}</span>
                  {badge != null && (
                    <span className={styles.navBadge}>{badge}</span>
                  )}
                </NavLink>
              ) : null
            )}
          </div>
        ))}

        {filteredGroups.length === 0 && (
          <p style={{
            textAlign:  'center',
            padding:    'var(--space-8) var(--space-4)',
            color:      'var(--text-muted)',
            fontSize:   'var(--font-size-xs)',
          }}>
            لا توجد نتائج
          </p>
        )}
      </div>

      {/* ── قسم المستخدم ── */}
      <div className={styles.userSection}>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>{initials}</div>
          <div className={styles.userMeta}>
            <span className={styles.userName}>{displayName}</span>
            {user?.role && (
              <span className={styles.userRole}>{user.role}</span>
            )}
          </div>
        </div>

        <button
          onClick={onLogout}
          className={styles.logoutBtn}
          type="button"
        >
          تسجيل الخروج
        </button>
      </div>
    </nav>
  );
};

export default Sidebar;